import Plot from "components/plotly/LocalisedPlot";
import { useTenantTranslation } from "hooks/formatters";
import { useMemo } from "react";
import {
  MISSING_DATA_KINDS,
  MISSING_DATA_KIND_COLORS,
  MISSING_DATA_KIND_TRANSLATION_KEYS,
  missingDataKindKey,
} from "../pipelineLevel";
import type { ComparisonMetric, ModelBucketGroup } from "./types";
import { computeYAxisMax } from "./yAxisRange";

type ModelComparisonChartProps = Readonly<{
  bucketGroups: readonly ModelBucketGroup[];
  selectedMetric: ComparisonMetric;
}>;

const sumBuckets = (
  group: ModelBucketGroup
): {
  totalVerified: number;
  matched: number;
  totalLabeller: number;
  totalDuration: number;
  durationCount: number;
} => {
  let totalVerified = 0;
  let matched = 0;
  let totalLabeller = 0;
  let totalDuration = 0;
  let durationCount = 0;
  for (const b of group.buckets) {
    totalVerified += b.total_verified_events;
    matched += b.matched_events;
    totalLabeller += b.total_labeller_events;
    totalDuration += b.total_event_duration_seconds ?? 0;
    durationCount += b.event_duration_count;
  }
  return {
    totalVerified,
    matched,
    totalLabeller,
    totalDuration,
    durationCount,
  };
};

export const ModelComparisonChart = ({
  bucketGroups,
  selectedMetric,
}: ModelComparisonChartProps) => {
  const { t } = useTenantTranslation();

  const labels: string[] = useMemo(
    () => bucketGroups.map((g) => g.model.labellingName),
    [bucketGroups]
  );

  const traces = useMemo(() => {
    if (selectedMetric === "fn_rate") {
      return buildFnRateTraces(bucketGroups, labels, t);
    }
    if (selectedMetric === "fp_rate") {
      return buildFpRateTraces(bucketGroups, labels, t);
    }
    if (selectedMetric === "mean_event_duration") {
      return buildMeanDurationTraces(bucketGroups, labels, t);
    }
    return buildJaccardTraces(bucketGroups, labels, t);
  }, [bucketGroups, labels, selectedMetric, t]);

  const yAxisMax = useMemo(
    () => computeYAxisMax(traces as { y?: number[] }[]),
    [traces]
  );

  return (
    <Plot
      data={traces}
      layout={{
        autosize: true,
        barmode: "stack",
        xaxis: { type: "category" },
        yaxis: {
          ticksuffix: selectedMetric === "mean_event_duration" ? "s" : "%",
          range: [0, yAxisMax],
        },
        margin: { l: 70, r: 120, t: 40, b: 60 },
        legend: {
          x: 1.02,
          y: 1,
          xanchor: "left" as const,
        },
        hovermode: "x unified" as const,
      }}
      config={{
        responsive: true,
        displayModeBar: true,
        displaylogo: false,
      }}
      style={{ width: "100%", height: "400px" }}
      useResizeHandler
    />
  );
};

const buildFnRateTraces = (
  bucketGroups: readonly ModelBucketGroup[],
  labels: string[],
  t: (key: string) => string
): Plotly.Data[] => {
  const traces: Plotly.Data[] = [];

  for (const kind of MISSING_DATA_KINDS) {
    const key = missingDataKindKey(kind);
    const yValues = bucketGroups.map((group) => {
      const { totalVerified } = sumBuckets(group);
      if (totalVerified === 0) return 0;
      let fnForKind = 0;
      for (const b of group.buckets) {
        if (missingDataKindKey(b.missing_data_kind ?? null) === key) {
          fnForKind += b.total_verified_events - b.matched_events;
        }
      }
      return (fnForKind / totalVerified) * 100;
    });

    if (yValues.some((v) => v > 0)) {
      const label = t(MISSING_DATA_KIND_TRANSLATION_KEYS[key] ?? "");
      traces.push({
        x: labels,
        y: yValues,
        name: label,
        type: "bar" as const,
        marker: { color: MISSING_DATA_KIND_COLORS[key] },
        hovertemplate: `%{x}<br>${label}: %{y:.1f}%<extra></extra>`,
      });
    }
  }

  return traces;
};

const buildFpRateTraces = (
  bucketGroups: readonly ModelBucketGroup[],
  labels: string[],
  t: (key: string) => string
): Plotly.Data[] => {
  const yValues = bucketGroups.map((group) => {
    const { totalLabeller, matched } = sumBuckets(group);
    if (totalLabeller === 0) return 0;
    return ((totalLabeller - matched) / totalLabeller) * 100;
  });

  return [
    {
      x: labels,
      y: yValues,
      name: t("falsePositiveRate"),
      type: "bar" as const,
      marker: { color: "#42a5f5" },
      hovertemplate: `%{x}<br>${t("falsePositiveRate")}: %{y:.1f}%<extra></extra>`,
    },
  ];
};

const buildJaccardTraces = (
  bucketGroups: readonly ModelBucketGroup[],
  labels: string[],
  t: (key: string) => string
): Plotly.Data[] => {
  const yValues = bucketGroups.map((group) => {
    const { totalVerified, matched, totalLabeller } = sumBuckets(group);
    const fn = totalVerified - matched;
    const fp = totalLabeller - matched;
    const denom = matched + fn + fp;
    if (denom === 0) return 0;
    return (matched / denom) * 100;
  });

  return [
    {
      x: labels,
      y: yValues,
      name: t("jaccardAccuracy"),
      type: "bar" as const,
      marker: { color: "#66bb6a" },
      hovertemplate: `%{x}<br>${t("jaccardAccuracy")}: %{y:.1f}%<extra></extra>`,
    },
  ];
};

const buildMeanDurationTraces = (
  bucketGroups: readonly ModelBucketGroup[],
  labels: string[],
  t: (key: string) => string
): Plotly.Data[] => {
  const yValues = bucketGroups.map((group) => {
    const { totalDuration, durationCount } = sumBuckets(group);
    if (durationCount === 0) return 0;
    return totalDuration / durationCount;
  });

  return [
    {
      x: labels,
      y: yValues,
      name: t("meanEventDuration"),
      type: "bar" as const,
      marker: { color: "#ab47bc" },
      hovertemplate: `%{x}<br>${t("meanEventDuration")}: %{y:.1f}s<extra></extra>`,
    },
  ];
};
